import React from 'react';
import { useLocation } from 'react-router-dom';
import { Topbar } from './Topbar.js';
import { Sidebar } from './Sidebar.js';
import { Breadcrumb } from './Breadcrumb.js';
import { CommandPalette } from './CommandPalette.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { ToastRegion, type ToastRegionProps } from '../../../shared/components/Toast.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useTranslation } from '../../../shared/i18n/index.js';
import './shell.css';

export interface AppShellProps {
  readonly children: React.ReactNode;
}

const ENV = (import.meta as unknown as { env?: { MODE?: string; VITE_APP_ENV?: string } }).env;

export function AppShell({ children }: AppShellProps): React.ReactElement {
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const mainRef = React.useRef<HTMLElement>(null);
  const [collapsed, setCollapsed] = React.useState(false);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [toasts, setToasts] = React.useState<ToastRegionProps['toasts']>([]);
  const dismissToast = React.useCallback(
    (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );

  React.useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // The content pane keeps its own scroll position, so a new screen would
  // otherwise open part-way down after clicking a menu item far down the list.
  React.useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const env = ENV?.VITE_APP_ENV ?? ENV?.MODE ?? 'development';
  const version = (ENV as { VITE_APP_VERSION?: string } | undefined)?.VITE_APP_VERSION ?? '0.0.0';

  return (
    <div
      data-testid="app-shell"
      className="tl-print-flow"
      style={{
        display: 'grid',
        gridTemplateAreas: `"topbar topbar" "sidebar main"`,
        gridTemplateColumns: 'auto 1fr',
        gridTemplateRows: '56px 1fr',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--color-bg-canvas)',
        color: 'var(--color-text-primary)',
      }}
    >
      <a
        href="#main"
        data-testid="skip-to-main"
        className="no-print"
        style={{
          position: 'absolute',
          left: -9999,
          top: 0,
          background: 'var(--color-brand-primary)',
          color: 'var(--color-text-inverse)',
          padding: 8,
          zIndex: 1000,
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '8px';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
        }}
      >
        Skip to main content
      </a>
      <Topbar
        env={env}
        version={version}
        onOpenPalette={() => setPaletteOpen(true)}
        onToggleSidebar={() => setCollapsed((v) => !v)}
      />
      <Sidebar collapsed={collapsed} />
      <main
        id="main"
        role="main"
        ref={mainRef}
        className="tl-print-flow"
        style={{ gridArea: 'main', padding: 16, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <Breadcrumb />
          <button
            type="button"
            data-testid="shell-sign-out"
            onClick={logout}
            style={{
              flex: 'none',
              height: 32,
              padding: '0 14px',
              color: 'var(--color-text-primary)',
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 5,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {t('dashboardPage.signOut')}
          </button>
        </div>
        <div className="tl-print-flow" style={{ marginTop: 12, flex: 1, minHeight: 0 }}>
          <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
        </div>
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
